/**
 * How a purchase is labelled wherever members or admins see one (My orders,
 * the member drawer): the tag's color says which product it is, its words
 * say how the member got it ("VIP · comped", "Membership · lifetime").
 * Wouter's call (2026-10-08): products keep their own colors, like
 * member-type pills.
 */

export type PurchaseProduct = 'membership' | 'vip' | 'parking' | 'children' | 'bulk'

/** How the member got it */
export type PurchaseAcquisition = 'paid' | 'discounted' | 'pre-paid' | 'comped' | 'refunded' | 'test' | 'lifetime'

interface PurchaseLike {
  status?: string
  pooled?: boolean
  issuedBy?: string
  orderId?: string
  eventbriteOrderId?: string
  isTestMode?: boolean
  memberDiscount?: unknown
}

const PRODUCT_LABELS: Record<PurchaseProduct, string> = {
  membership: 'Membership',
  vip: 'VIP',
  parking: 'Parking',
  children: 'Children',
  bulk: 'Bulk',
}

export function productLabel(type: string): string {
  return PRODUCT_LABELS[type as PurchaseProduct] ?? type
}

/** The tag class for a product's color (`.kb-product-*` in phosphor.css) */
export function productTagClass(type: string): string {
  return type in PRODUCT_LABELS ? `kb-product-${type}` : 'kb-tag-neutral'
}

/** Admin comps from before `issuedBy` existed have an "admin-…" order id */
export function isLegacyAdminComp(purchase: PurchaseLike): boolean {
  return Boolean(purchase.orderId?.startsWith('admin-') || purchase.eventbriteOrderId?.startsWith('admin-'))
}

export function isRefunded(purchase: PurchaseLike): boolean {
  return purchase.status === 'refunded' || purchase.status === 'partially_refunded'
}

export function acquisitionOf(purchase: PurchaseLike): PurchaseAcquisition {
  if (isRefunded(purchase)) return 'refunded'
  if (purchase.isTestMode) return 'test'
  if (purchase.pooled) return 'pre-paid'
  if (purchase.issuedBy || isLegacyAdminComp(purchase)) return 'comped'
  if (purchase.memberDiscount) return 'discounted'
  return 'paid'
}

/** "VIP · comped" */
export function purchaseTagText(type: string, acquisition: PurchaseAcquisition): string {
  return `${productLabel(type)} · ${acquisition}`
}
